import { Dispatch, SetStateAction, useCallback, useMemo } from "react"

export type ActionMarkerStore<V> = Map<string, Map<string, V>>

export interface ActionMarkers<V> {
  set(conversationId: string, messageId: string, value: V): void
  clear(conversationId: string, messageId: string): void
  get(conversationId: string, messageId: string): V | undefined
  has(conversationId: string, messageId: string): boolean
}

export function useActionMarkers<V>(
  markers: ActionMarkerStore<V>,
  setMarkers: Dispatch<SetStateAction<ActionMarkerStore<V>>>,
): ActionMarkers<V> {
  const set = useCallback((conversationId: string, messageId: string, value: V) => {
    setMarkers(prev => {
      const next = new Map(prev)
      const existing = next.get(conversationId)
      const updated = existing ? new Map(existing) : new Map<string, V>()

      updated.set(messageId, value)
      next.set(conversationId, updated)

      return next
    })
  }, [setMarkers])

  const clear = useCallback((conversationId: string, messageId: string) => {
    setMarkers(prev => {
      const existing = prev.get(conversationId)
      if (!existing?.has(messageId)) return prev

      const next = new Map(prev)
      const updated = new Map(existing)
      updated.delete(messageId)

      if (updated.size === 0) {
        next.delete(conversationId)
      } else {
        next.set(conversationId, updated)
      }

      return next
    })
  }, [setMarkers])

  const get = useCallback((conversationId: string, messageId: string): V | undefined => {
    return markers.get(conversationId)?.get(messageId)
  }, [markers])

  const has = useCallback((conversationId: string, messageId: string): boolean => {
    return markers.get(conversationId)?.has(messageId) ?? false
  }, [markers])

  return useMemo(() => ({ set, clear, get, has }), [set, clear, get, has])
}
